const schema = (props) => {
  const { intl } = props;

  return {
    title: 'Hero Block',
    fieldsets: [
      {
        id: 'default',
        title: 'Hero Options',
        fields: [
          'isFullWidth',
          'multipleHeroes',
          'displayMode',
          'carouselEnabled',
          'carouselDisplayMode',
          'layoutMode',
          'customClass',
        ],
      },
      {
        id: 'firstHero',
        title: 'First Hero',
        fields: [
          'heroId',
          'usePageTitle',
          'title',
          'usePageDescription',
          'subtitle',
          'contentBlocks',
          'preheader',
          'usePreviewImage',
          'backgroundImage',
          'backgroundVideo',
          'backgroundColor',
          'foregroundColor',
          'showPublicationDate',
          'topPaddingDesktop',
          'topPaddingMobile',
          'alignment',
          'horizontalLayout',
          'useTOC',
          'useH2',
          'useH3',
          'useBlockTitles',
          'buttons',
          'smallButtons',
        ],
      },
      {
        id: 'imageStack',
        title: 'Decorative Images',
        fields: ['decorativeImage1', 'decorativeImage2', 'decorativeImage3'],
      },
      {
        id: 'heroes',
        title: 'Additional Heroes',
        fields: ['heroes'],
      },
      {
        id: 'carousel',
        title: 'Carousel Settings',
        fields: [
          'carouselAutoplay',
          'carouselAutoplayDelay',
          'carouselHideNav',
          'carouselHideButtons',
          'carouselControlsColor',
        ],
      },
    ],
    properties: {
      isFullWidth: {
        title: 'Full width background',
        type: 'boolean',
        default: false,
      },
      multipleHeroes: {
        title: 'Enable multiple heroes',
        type: 'boolean',
        default: false,
      },
      displayMode: {
        title: 'Display mode',
        type: 'string',
        choices: [
          ['individual', 'Individual backgrounds per hero'],
          ['shared', 'Shared background from first hero'],
        ],
        default: 'individual',
      },
      carouselEnabled: {
        title: 'Enable carousel mode',
        description: 'Display only additional heroes in a carousel (first hero is hidden)',
        type: 'boolean',
        default: false,
      },
      carouselDisplayMode: {
        title: 'Carousel background mode',
        type: 'string',
        choices: [
          ['individual', 'Individual backgrounds per slide'],
          ['shared', 'Shared background from first hero'],
        ],
        default: 'individual',
      },
      layoutMode: {
        title: 'Layout mode',
        type: 'string',
        choices: [
          ['standard', 'Standard (full width text)'],
          ['imageStackRight', 'Image Stack (text left, images right)'],
          ['imageStackLeft', 'Image Stack (text right, images left)'],
        ],
        default: 'standard',
      },
      customClass: {
        title: 'Custom CSS class',
        type: 'string',
        description: 'Additional class to add to the hero block',
      },
      heroId: {
        title: 'Hero ID',
        type: 'string',
        description: 'Unique ID for anchor links (e.g., "overview" for #overview)',
      },
      usePageTitle: {
        title: 'Use page title as first hero title',
        type: 'boolean',
        default: false,
      },
      title: {
        title: 'Title',
        type: 'string',
      },
      usePageDescription: {
        title: 'Use page description as first hero subtitle',
        type: 'boolean',
        default: false,
      },
      subtitle: {
        title: 'Subtitle',
        type: 'string',
      },
      contentBlocks: {
        title: 'Content blocks',
        description: 'Add rich text between subtitle and buttons',
        widget: 'slate_richtext',
      },
      preheader: {
        title: 'Preheader',
        type: 'string',
        description: 'Text that appears above the title',
      },
      usePreviewImage: {
        title: 'Use page preview image for first hero',
        type: 'boolean',
        default: false,
      },
      backgroundImage: {
        title: 'Background image',
        widget: 'object_browser',
        mode: 'image',
        allowExternals: false,
      },
      backgroundVideo: {
        title: 'Background video',
        widget: 'object_browser',
        mode: 'file',
        allowExternals: false,
        selectableTypes: ['File'],
        description: 'MP4 only. Will autoplay, loop, and stay muted.',
      },
      backgroundColor: {
        title: 'Background color overlay',
        choices: [
          ['', 'Gradient (default)'],
          ['none', 'None'],
          ['rgba(0, 0, 0, 0.3)', 'Black - Light (30%)'],
          ['rgba(0, 0, 0, 0.5)', 'Black - Medium (50%)'],
          ['rgba(0, 0, 0, 0.7)', 'Black - Dark (70%)'],
          ['rgba(255, 255, 255, 0.3)', 'White - Light (30%)'],
          ['rgba(255, 255, 255, 0.5)', 'White - Medium (50%)'],
          ['rgba(255, 255, 255, 0.7)', 'White - Dark (70%)'],
          ['var(--blue-tint-10)', 'Blue tint'],
          ['var(--teal-base)', 'Teal'],
          ['var(--teal-tint-10)', 'Teal tint'],
          ['var(--teal-tint-20)', 'Teal tint two'],
          ['var(--turquoise-tint-10)', 'Turquoise tint'],
          ['var(--gold-tint-10)', 'Gold tint'],
          ['var(--coral-tint-20)', 'Coral tint'],
        ],
      },
      foregroundColor: {
        title: 'Foreground color',
        description: 'Color scheme for text and buttons',
        choices: [
          ['light', 'Light (white text)'],
          ['dark', 'Dark (black text)'],
        ],
        default: 'light',
      },
      showPublicationDate: {
        title: 'Show publication date on first hero',
        type: 'boolean',
        default: false,
      },
      topPaddingDesktop: {
        title: 'Top padding (Desktop)',
        choices: [
          ['compact', 'Compact (4rem)'],
          ['default', 'Default (8rem)'],
          ['spacious', 'Spacious (12rem)'],
          ['extra-spacious', 'Extra Spacious (16rem)'],
        ],
        default: 'default',
      },
      topPaddingMobile: {
        title: 'Top padding (Mobile)',
        choices: [
          ['compact', 'Compact (4rem)'],
          ['default', 'Default (8rem)'],
          ['spacious', 'Spacious (12rem)'],
        ],
        default: 'default',
      },
      alignment: {
        title: 'Text alignment',
        widget: 'align',
        actions: ['left', 'center', 'right'],
        default: 'left',
      },
      horizontalLayout: {
        title: 'Display text and buttons side by side',
        type: 'boolean',
        default: false,
      },
      useTOC: {
        title: 'Use TOC instead of buttons',
        type: 'boolean',
        default: false,
      },
      useH2: {
        title: 'Include H2 headings in TOC',
        type: 'boolean',
        default: true,
      },
      useH3: {
        title: 'Include H3 headings in TOC',
        type: 'boolean',
        default: true,
      },
      useBlockTitles: {
        title: 'Include block titles in TOC',
        description: 'Include titles from listing blocks, carousels, and other blocks',
        type: 'boolean',
        default: false,
      },
      buttons: {
        title: 'Buttons',
        widget: 'object_list',
        schema: {
          title: 'Button',
          fieldsets: [
            {
              id: 'default',
              title: 'Default',
              fields: ['label', 'link', 'buttonStyle'],
            },
          ],
          properties: {
            label: {
              title: 'Label',
              type: 'string',
            },
            link: {
              title: 'Link',
              widget: 'object_browser',
              mode: 'link',
              allowExternals: true,
              multi: false,
              default: null,
            },
            buttonStyle: {
              title: 'Button style',
              choices: [
                ['white-outline', 'White outline'],
                ['white-filled', 'White filled'],
                ['black-outline', 'Black outline'],
                ['black-filled', 'Black filled'],
                ['primary-outline', 'Primary outline'],
                ['primary-filled', 'Primary filled'],
                ['secondary-outline', 'Secondary outline'],
                ['secondary-filled', 'Secondary filled'],
                ['teal-outline', 'Teal outline'],
                ['teal-filled', 'Teal filled'],
                ['turquoise-outline', 'Turquoise outline'],
                ['turquoise-filled', 'Turquoise filled'],
              ],
              default: 'white-outline',
            },
          },
          required: [],
        },
      },
      smallButtons: {
        title: 'Use smaller buttons',
        type: 'boolean',
        default: false,
      },
      decorativeImage1: {
        title: 'Decorative image 1',
        widget: 'object_browser',
        mode: 'image',
        allowExternals: false,
        description: 'First image in the stack (top layer)',
      },
      decorativeImage2: {
        title: 'Decorative image 2',
        widget: 'object_browser',
        mode: 'image',
        allowExternals: false,
        description: 'Second image in the stack (middle layer)',
      },
      decorativeImage3: {
        title: 'Decorative image 3',
        widget: 'object_browser',
        mode: 'image',
        allowExternals: false,
        description: 'Third image in the stack (bottom layer)',
      },
      heroes: {
        title: 'Heroes',
        widget: 'object_list',
        schema: {
          title: 'Hero',
          fieldsets: [
            {
              id: 'content',
              title: 'Content',
              fields: ['heroId', 'preheader', 'title', 'subtitle', 'contentBlocks', 'topPaddingDesktop', 'topPaddingMobile', 'alignment', 'horizontalLayout', 'buttons', 'smallButtons'],
            },
            {
              id: 'background',
              title: 'Background',
              fields: ['backgroundImage', 'backgroundVideo', 'backgroundColor', 'foregroundColor'],
            },
          ],
          properties: {
            heroId: {
              title: 'Hero ID',
              type: 'string',
              description: 'Unique ID for anchor links (e.g., "services" for #services)',
            },
            preheader: {
              title: 'Preheader',
              type: 'string',
            },
            title: {
              title: 'Title',
              type: 'string',
            },
            subtitle: {
              title: 'Subtitle',
              type: 'string',
            },
            contentBlocks: {
              title: 'Content blocks',
              description: 'Add rich text between subtitle and buttons',
              widget: 'slate_richtext',
            },
            topPaddingDesktop: {
              title: 'Top padding (Desktop)',
              choices: [
                ['compact', 'Compact (4rem)'],
                ['default', 'Default (8rem)'],
                ['spacious', 'Spacious (12rem)'],
                ['extra-spacious', 'Extra Spacious (16rem)'],
              ],
              default: 'default',
            },
            topPaddingMobile: {
              title: 'Top padding (Mobile)',
              choices: [
                ['compact', 'Compact (4rem)'],
                ['default', 'Default (8rem)'],
                ['spacious', 'Spacious (12rem)'],
              ],
              default: 'default',
            },
            alignment: {
              title: 'Text alignment',
              widget: 'align',
              actions: ['left', 'center', 'right'],
              default: 'left',
            },
            horizontalLayout: {
              title: 'Display text and buttons side by side',
              type: 'boolean',
              default: false,
            },
            buttons: {
              title: 'Buttons',
              widget: 'object_list',
              schema: {
                title: 'Button',
                fieldsets: [
                  {
                    id: 'default',
                    title: 'Default',
                    fields: ['label', 'link', 'buttonStyle'],
                  },
                ],
                properties: {
                  label: {
                    title: 'Label',
                    type: 'string',
                  },
                  link: {
                    title: 'Link',
                    widget: 'object_browser',
                    mode: 'link',
                    allowExternals: true,
                    multi: false,
                    default: null,
                  },
                  buttonStyle: {
                    title: 'Button style',
                    choices: [
                      ['white-outline', 'White outline'],
                      ['white-filled', 'White filled'],
                      ['black-outline', 'Black outline'],
                      ['black-filled', 'Black filled'],
                      ['primary-outline', 'Primary outline'],
                      ['primary-filled', 'Primary filled'],
                      ['secondary-outline', 'Secondary outline'],
                      ['secondary-filled', 'Secondary filled'],
                      ['teal-outline', 'Teal outline'],
                      ['teal-filled', 'Teal filled'],
                      ['turquoise-outline', 'Turquoise outline'],
                      ['turquoise-filled', 'Turquoise filled'],
                    ],
                    default: 'white-outline',
                  },
                },
                required: [],
              },
            },
            smallButtons: {
              title: 'Use smaller buttons',
              type: 'boolean',
              default: false,
            },
            backgroundImage: {
              title: 'Background image',
              widget: 'object_browser',
              mode: 'image',
              allowExternals: false,
            },
            backgroundVideo: {
              title: 'Background video',
              widget: 'object_browser',
              mode: 'file',
              allowExternals: false,
              selectableTypes: ['File'],
              description: 'MP4 only. Will autoplay, loop, and stay muted.',
            },
            backgroundColor: {
              title: 'Background color overlay',
              choices: [
                ['', 'Gradient (default)'],
                ['none', 'None'],
                ['rgba(0, 0, 0, 0.3)', 'Black - Light (30%)'],
                ['rgba(0, 0, 0, 0.5)', 'Black - Medium (50%)'],
                ['rgba(0, 0, 0, 0.7)', 'Black - Dark (70%)'],
                ['rgba(255, 255, 255, 0.3)', 'White - Light (30%)'],
                ['rgba(255, 255, 255, 0.5)', 'White - Medium (50%)'],
                ['rgba(255, 255, 255, 0.7)', 'White - Dark (70%)'],
                ['var(--blue-tint-10)', 'Blue tint'],
                ['var(--teal-base)', 'Teal'],
                ['var(--teal-tint-10)', 'Teal tint'],
                ['var(--teal-tint-20)', 'Teal tint two'],
                ['var(--turquoise-tint-10)', 'Turquoise tint'],
                ['var(--gold-tint-10)', 'Gold tint'],
                ['var(--coral-tint-20)', 'Coral tint'],
              ],
            },
            foregroundColor: {
              title: 'Foreground color',
              description: 'Color scheme for text and buttons',
              choices: [
                ['light', 'Light (white text)'],
                ['dark', 'Dark (black text)'],
              ],
              default: 'light',
            },
          },
          required: [],
        },
      },
      carouselAutoplay: {
        title: 'Enable autoplay',
        type: 'boolean',
        default: false,
      },
      carouselAutoplayDelay: {
        title: 'Autoplay delay (milliseconds)',
        type: 'number',
        default: 5000,
        description: 'Time between automatic slide transitions',
      },
      carouselHideNav: {
        title: 'Hide navigation dots',
        type: 'boolean',
        default: false,
      },
      carouselHideButtons: {
        title: 'Hide prev/next buttons',
        type: 'boolean',
        default: false,
      },
      carouselControlsColor: {
        title: 'Controls color scheme',
        choices: [
          ['light', 'Light (white)'],
          ['dark', 'Dark (black)'],
        ],
        default: 'light',
      },
    },
    required: [],
  };
};

export default schema;
